import { ReactNode } from "react";
import { MobileBottomNav } from "./MobileBottomNav";
import { Button } from "@/components/ui/button";
import { Bell, Download, LogOut, Settings, Smartphone, Moon, Sun } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/contexts/ThemeContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface MobileLayoutProps {
  children: ReactNode;
}

export function MobileLayout({ children }: MobileLayoutProps) {
  const { user } = useAuth();
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const getInitials = (email: string | undefined) => {
    if (!email) return "?";
    return email.substring(0, 2).toUpperCase();
  };

  const handleInstall = async () => {
    const success = await promptInstall();
    if (!success) navigate("/install");
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn("Sign out error (clearing local session anyway):", err);
    }
    localStorage.removeItem("sb-uqcuajozfzvoiomyxnbg-auth-token");
    toast.success("Signed out");
    navigate("/auth");
  };

  return (
    <div className="flex flex-col min-h-screen w-full max-w-full overflow-x-hidden bg-background">
      <header
        className="sticky top-0 z-40 border-b border-border/40 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="flex items-center justify-between px-4 py-2.5">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2.5 active:scale-95 transition-transform"
          >
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-md">
              <span className="text-primary-foreground font-bold text-sm tracking-tight">RG</span>
            </div>
            <div className="leading-tight text-left">
              <div className="text-sm font-bold tracking-tight">RoofGenius</div>
              <div className="text-[10px] text-muted-foreground -mt-0.5">Field OS</div>
            </div>
          </button>

          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full">
              <Bell className="h-[18px] w-[18px]" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full p-0">
                  <Avatar className="h-8 w-8 ring-2 ring-primary/20">
                    <AvatarFallback className="text-xs bg-primary/10 text-primary font-semibold">
                      {getInitials(user?.email)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">Account</span>
                    <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {!isInstalled && (
                  <DropdownMenuItem onClick={handleInstall}>
                    {isInstallable ? (
                      <Download className="w-4 h-4 mr-2" />
                    ) : (
                      <Smartphone className="w-4 h-4 mr-2" />
                    )}
                    Install App
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={toggleTheme}>
                  {theme === "dark" ? (
                    <Sun className="w-4 h-4 mr-2" />
                  ) : (
                    <Moon className="w-4 h-4 mr-2" />
                  )}
                  {theme === "dark" ? "Light Mode" : "Dark Mode"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-full overflow-y-auto overflow-x-hidden pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))]">
        <div className="w-full max-w-full">{children}</div>
      </main>

      <MobileBottomNav />
    </div>
  );
}
